/**
 * Admin-ops contracts — slice 8. Tenant correction, credit top-ups, the audit
 * row every staff write leaves behind, and the cross-tenant reconciliation
 * report that closes slice 6 criterion 6.
 *
 * This file is a **leaf**. It imports `./tenant.js` and `./credit.js`; neither
 * of them imports it, and nothing else in this package does either, so it is
 * exported last from `../index.js`. That is not tidiness — slice 10 and slice 12
 * both documented the same failure, and it is worth repeating because it does
 * not look like a bug when you write it: two modules that both build zod schemas
 * at load time and import each other fail **at import**, as `z.enum(undefined)`,
 * not at review. Every arrow here points down into contracts that were fully
 * evaluated before this module body ran.
 *
 * WHY THESE SHAPES ARE PARANOID
 * ----------------------------------------------------------------------------
 * Every route these contracts describe runs on the `wend_admin` connection,
 * which is `BYPASSRLS`. It is the one role in the system that can see and write
 * every tenant. RLS — the safeguard the whole codebase leans on — is switched
 * off here by design, so the guarantees it normally provides have to be carried
 * by the *shape of the request* instead. That is the single idea behind
 * everything below.
 *
 * Three consequences, each of which is a decision from SLICES.md slice 8 made
 * structural rather than left to a service check:
 *
 *   1. **An un-editable field is unrepresentable, not ignored.**
 *      {@link tenantEditableFieldsSchema} is `.strict()`, so a body naming
 *      `subscriptionKey`, `id`, `createdByAdminId` or anything else outside the
 *      list **fails parsing**. The rejected alternative was `.partial()` over
 *      the whole tenant shape with a service-side allow-list: zod strips unknown
 *      keys by default, so that design answers `200 OK` to a request that asked
 *      for a change it did not make. Telling a caller their change succeeded
 *      when it was dropped is worse than refusing it, and it is worse
 *      specifically here, because the caller is staff acting on somebody else's
 *      workspace and will not be the one who notices.
 *
 *   2. **A reason is required on every mutation, and it is not defaultable.**
 *      {@link adminReasonSchema} has no `.default()` and no `.optional()`
 *      anywhere it is used. "Why does this tenant have 50,000 credits" is the
 *      exact question the field exists to answer, and an optional reason is a
 *      reason nobody fills in on the day it would have mattered.
 *
 *   3. **A top-up is a ledger movement and cannot be anything else.**
 *      {@link tenantTopupRequestSchema} carries a channel, a positive amount, a
 *      reason and an idempotency token. It has no `balance` field, and there is
 *      no contract in this file that expresses writing `credit_balances`. The
 *      response reports `applied` — whether *this* call inserted the row —
 *      precisely so a replay can be seen to have moved nothing.
 *
 * WHAT STAFF MAY NOT TOUCH, WRITTEN DOWN
 * ----------------------------------------------------------------------------
 * `BYPASSRLS` *could* reach a tenant's contacts, templates, campaigns, segments
 * and SES/Meta credentials. That is exactly why the boundary is a constant here
 * ({@link TENANT_DATA_ADMIN_MAY_NOT_REACH}) rather than an assumption: a written
 * boundary can be asserted by a verify script, and an assumed one is discovered
 * by an incident. Nothing in this file describes reading or writing any of it.
 *
 * `subscriptionKey` is **deliberately not editable**, and that is the one call
 * in this slice worth defending at length. It is `UNIQUE` across every tenant
 * and it is the value support asks a customer to read back, so changing it does
 * two things at once: it invalidates whatever the customer is holding, and it
 * can collide, surfacing as a constraint violation from the driver rather than
 * as a field error on a form. Bundling it into a general PATCH means the request
 * that fixes a postal code is the same request that can cut a customer's access.
 * Rotating a subscription key is a different operation with a different blast
 * radius; when it is needed it earns its own route, its own confirmation and its
 * own audit action. Until then, a body that names it fails parsing — which is a
 * far better outcome than a body that names it and is quietly obeyed.
 *
 * ABSENT, CLEARED AND UNCHANGED ARE THREE DIFFERENT THINGS
 * ----------------------------------------------------------------------------
 * The same zero-versus-absent rule the rest of this codebase lives by, applied
 * to a PATCH body:
 *
 *   - **key absent**   ⇒ leave the column alone
 *   - **`null`**       ⇒ clear the column (only where the column is nullable)
 *   - **a value**      ⇒ set the column
 *
 * `.partial()` alone would have collapsed the first two together and left staff
 * with no way to blank out a website they had entered by mistake. Only the
 * columns that are genuinely `NULL`-able in `tenants` accept `null` — `name`,
 * `contact_email`, `contact_phone`, `valid_till` and `is_active` are `NOT NULL`,
 * so a `null` for them is refused here rather than by the driver, where the
 * error would name a constraint instead of a field.
 *
 * A RECONCILIATION REPORTS; IT DOES NOT REPAIR
 * ----------------------------------------------------------------------------
 * There is no `repaired` field, no `fixed` count and no shape in this file that
 * can say a drift was corrected. That absence is the contract. A ledger and a
 * balance disagreeing is either a bug we need to see or a manual correction
 * somebody made, and a job that quietly repairs both makes the first invisible
 * and the second unattributable. If a cache genuinely needs rebuilding, a person
 * does it, through a route that writes an audit row with a reason.
 *
 * The report can also say **"I did not check this tenant"** distinctly from
 * "I checked it and it agreed" — {@link tenantReconciliationSchema} is a
 * discriminated union, and the `not_checked` member carries a *required* reason.
 * A run that skipped four tenants because their query timed out and reported
 * "zero drift" is the failure this shape exists to make impossible.
 */
import { z } from 'zod';

import {
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  uuidSchema,
} from '../common.js';
import { channelSchema, type Feature } from '../enums.js';
import {
  creditBalanceSchema,
  creditIdempotencyKeySchema,
  creditReconciliationSchema,
  type CreditReconciliation,
} from './credit.js';
import {
  createTenantRequestSchema,
  tenantOrganisationSchema,
  tenantSchema,
  type TenantOrganisation,
} from './tenant.js';

/** Compile-time assertion. `AssertTrue<false>` is a type error. */
type AssertTrue<T extends true> = T;

/* -------------------------------------------------------------------------- */
/* The reason — required on every mutation                                     */
/* -------------------------------------------------------------------------- */

/**
 * The shortest thing that can be an explanation.
 *
 * `.min(1)` would satisfy the letter of "non-empty" and the spirit of nothing —
 * `.` is one character. Eight is the shortest run that can hold a word and a
 * number ("topup 5k", "typo fix"), so it rejects the keyboard mash without
 * rejecting a terse operator who is telling the truth.
 */
export const MIN_ADMIN_REASON_LENGTH = 8;

/**
 * The cap on a reason, matching the column it is stored in.
 *
 * `admin_audit.reason` is where a tenant edit's explanation lands, and 0015's
 * `admin_audit_reason_is_an_explanation` is `CHECK (length(btrim(reason))
 * BETWEEN 8 AND 500)`. This constant and that CHECK are the same two numbers, so
 * a reason this schema accepts is a reason the column takes.
 *
 * **Superseded reasoning, kept because it argues for something that is not
 * true.** This used to be `MAX_CREDIT_NOTE_LENGTH`, justified by "a top-up's
 * reason is written onto the ledger entry's note". *There is no such column.*
 * 0011 did not add a `note` to `credit_ledger`, slice 8 did not either, and
 * `CreditsService.listLedger` says so in as many words — every ledger row it
 * reads carries `note: null`. Deriving from a column that does not exist meant
 * that raising `MAX_CREDIT_NOTE_LENGTH` for an unrelated reason would silently
 * raise this cap too, past the CHECK above and past
 * {@link MAX_TOPUP_REASON_LENGTH} below, in both cases producing exactly the
 * "accepted and then refused" failure the old comment claimed to prevent.
 *
 * This is **not** the cap that binds a top-up. See {@link MAX_TOPUP_REASON_LENGTH}.
 */
export const MAX_ADMIN_REASON_LENGTH = 500;

/**
 * The cap on a **top-up's** reason, which is lower, and why.
 *
 * A top-up's explanation is not stored in `admin_audit` — the movement runs on
 * the RLS-enforced `wend_app` connection and 0015 revokes everything on
 * `admin_audit` from that role, so the grant is audited into `audit_log` in the
 * movement's own transaction instead (`admin-credits.service.ts` argues it in
 * full). `audit_log.metadata` holds **labels, not payloads**: the writer refuses
 * any value over 256 characters, and it refuses by throwing — which inside the
 * top-up's transaction is a rolled-back grant reported as an unactionable 500.
 *
 * So the cap lives here, in the contract both apps parse against, rather than
 * only in the service. The failure it removes is specific and was real: the
 * admin app's one reason control is shared by the edit form and the top-up form,
 * so a 300-character explanation typed on the top-up form counted itself up to
 * "300 / 500", passed client-side validation, passed the two-step review, and
 * was refused by the API — the identical text saving without complaint on the
 * form directly above it. A cap only one side knows about is a cap discovered by
 * hitting it.
 *
 * When the `wend_credit_granter` role lands and the grant is audited into
 * `admin_audit` like every other staff write, this becomes
 * {@link MAX_ADMIN_REASON_LENGTH} and {@link topupReasonSchema} goes away.
 */
export const MAX_TOPUP_REASON_LENGTH = 256;

/**
 * Why a staff member made this change. **Required everywhere it appears.**
 *
 * There is no `.optional()` and no `.default()` on this schema, and there must
 * never be one. A default reason ("admin change") is indistinguishable from a
 * reason nobody typed, which makes every audit row equally uninformative
 * including the one somebody is actually investigating.
 *
 * `.trim()` runs before `.min()`, so a body of spaces is refused rather than
 * stored as a reason of length zero.
 */
export const adminReasonSchema = z
  .string()
  .trim()
  .min(MIN_ADMIN_REASON_LENGTH, `Say why, in at least ${String(MIN_ADMIN_REASON_LENGTH)} characters`)
  .max(MAX_ADMIN_REASON_LENGTH);
export type AdminReason = z.infer<typeof adminReasonSchema>;

/**
 * The same reason, capped where a top-up can actually record it.
 *
 * A narrowing of {@link adminReasonSchema} rather than a second hand-written
 * schema, so the minimum, the trim and the message stay one decision. The
 * message names the number instead of leaving zod to say "String must contain at
 * most 256 character(s)" — the operator is being asked to shorten a sentence
 * they were told to write in full, so the refusal owes them the target.
 */
export const topupReasonSchema = adminReasonSchema.max(
  MAX_TOPUP_REASON_LENGTH,
  `Keep a top-up's reason under ${String(MAX_TOPUP_REASON_LENGTH)} characters — it is written to the audit log, which refuses a longer value rather than storing half of it`,
);

/* -------------------------------------------------------------------------- */
/* The boundary, written down                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Tenant columns staff may **not** change through the admin surface.
 *
 * A constant rather than a comment so a verify script can walk it and assert
 * that each one is refused, which is the only version of this boundary that
 * stays true. `.strict()` on {@link tenantEditableFieldsSchema} is what does the
 * refusing; this list is what proves it still does.
 *
 * `subscriptionKey` — see the file header. `id`, `createdAt`, `updatedAt` and
 * `createdByAdminId` are the row's own provenance; a mutable provenance is not
 * provenance.
 */
export const TENANT_FIELDS_ADMIN_MAY_NOT_CHANGE = [
  'id',
  'subscriptionKey',
  'createdByAdminId',
  'createdAt',
  'updatedAt',
] as const;
export type TenantFieldAdminMayNotChange = (typeof TENANT_FIELDS_ADMIN_MAY_NOT_CHANGE)[number];

/**
 * Tenant-owned data no admin route may read or write, whatever `BYPASSRLS`
 * makes technically possible.
 *
 * SLICES.md slice 8, criterion 7. Listed here rather than left to prose because
 * "no admin route can read a tenant's contacts" is a claim a test can only make
 * against a list, and because the day somebody adds `GET
 * /admin/tenants/:id/contacts` "just for support", this constant is the thing
 * the review has to argue with.
 */
export const TENANT_DATA_ADMIN_MAY_NOT_REACH = [
  'contacts',
  'datasets',
  'segments',
  'templates',
  'campaigns',
  'messages',
  'integrations',
  'suppressions',
] as const;
export type TenantDataAdminMayNotReach = (typeof TENANT_DATA_ADMIN_MAY_NOT_REACH)[number];

/* -------------------------------------------------------------------------- */
/* The editable surface                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A column that is `NULL`-able in `tenants`: absent leaves it, `null` clears it,
 * a value sets it.
 *
 * Written as a helper rather than spelled out per field so the three-state
 * meaning is stated once. Applied only to the nullable columns — see the file
 * header for why `name`, `email`, `phone`, `validTill` and `isActive` are not in
 * its scope.
 */
function clearable<T extends z.ZodTypeAny>(schema: T): z.ZodOptional<z.ZodNullable<T>> {
  return schema.nullable().optional();
}

/**
 * The organisation half of a tenant edit.
 *
 * Every validator is taken from {@link tenantOrganisationSchema}`.shape` rather
 * than retyped, so the rules that govern a legal name at creation are the same
 * rules that govern correcting one. Retyping them is how a `max(200)` becomes a
 * `max(255)` in one of two places and a support ticket in the other.
 *
 * `.strict()` here as well as at the top level: nesting is where an allow-list
 * usually leaks, because the outer object is checked and the inner one is not.
 */
export const tenantOrganisationEditSchema = z
  .object({
    legalName: clearable(tenantOrganisationSchema.shape.legalName),
    website: clearable(tenantOrganisationSchema.shape.website),
    addressLine1: clearable(tenantOrganisationSchema.shape.addressLine1),
    addressLine2: clearable(tenantOrganisationSchema.shape.addressLine2),
    city: clearable(tenantOrganisationSchema.shape.city),
    state: clearable(tenantOrganisationSchema.shape.state),
    postalCode: clearable(tenantOrganisationSchema.shape.postalCode),
    country: clearable(tenantOrganisationSchema.shape.country),
    taxId: clearable(tenantOrganisationSchema.shape.taxId),
  })
  .strict();
export type TenantOrganisationEdit = z.infer<typeof tenantOrganisationEditSchema>;

/**
 * Fails to compile if a column is added to {@link tenantOrganisationSchema} and
 * not to the edit shape above. A new organisation field that silently cannot be
 * corrected is the quietest kind of gap: the form renders it, the PATCH refuses
 * it, and nobody finds out until a customer asks.
 *
 * The key sets are compared in both directions. The second one catches the
 * opposite drift — an editable key that no longer exists on the tenant — which
 * would be a field the form can send, the parser accepts, and the update has
 * nowhere to write.
 */
type _OrganisationEditCoversEveryColumn = AssertTrue<
  keyof TenantOrganisation extends keyof TenantOrganisationEdit ? true : false
>;
type _OrganisationEditInventsNoColumn = AssertTrue<
  keyof TenantOrganisationEdit extends keyof TenantOrganisation ? true : false
>;

/**
 * **Everything staff may change about a tenant, and nothing else.**
 *
 * `.strict()` is the enforcement. A body carrying `subscriptionKey` does not get
 * a `200` with the key untouched; it gets a validation failure naming the key it
 * was not allowed to send. See the file header for why that difference is the
 * whole point of this file.
 *
 * The field names are the *wire* names — `email`, `phone`, `organisation` — the
 * same ones {@link tenantSchema} reads back and {@link createTenantRequestSchema}
 * writes in, not the database's `contact_email` / `contact_phone`. A third
 * spelling for the same value across create, read and update is the sort of
 * near-miss that produces a form that saves to the wrong field.
 *
 * `features` **replaces** the enabled set; it does not merge into it. That is
 * stated here because the merging interpretation is the one that reads naturally
 * from an array and is impossible to recover from: under a merge there is no
 * request that can ever turn a feature *off*. `min(1)` is inherited from
 * creation, so "a tenant entitled to nothing" stays unrepresentable.
 */
export const tenantEditableFieldsSchema = z
  .object({
    /** Workspace name shown in the product. `NOT NULL` — cannot be cleared. */
    name: createTenantRequestSchema.shape.name.optional(),
    /** Billing/ops contact, not a login. `NOT NULL` — cannot be cleared. */
    email: createTenantRequestSchema.shape.email.optional(),
    /** E.164. `NOT NULL` — cannot be cleared. */
    phone: createTenantRequestSchema.shape.phone.optional(),
    organisation: tenantOrganisationEditSchema.optional(),
    /** Subscription expiry. `NOT NULL` — an open-ended subscription is a date, not a null. */
    validTill: createTenantRequestSchema.shape.validTill.optional(),
    /** Suspends the workspace. `NOT NULL` — there is no third state. */
    isActive: z.boolean().optional(),
    /** The complete enabled set, replacing what is there. See above. */
    features: createTenantRequestSchema.shape.features.optional(),
  })
  .strict();
export type TenantEditableFields = z.infer<typeof tenantEditableFieldsSchema>;

/* -------------------------------------------------------------------------- */
/* Field paths — the audit vocabulary, tied to the editable surface            */
/* -------------------------------------------------------------------------- */

/**
 * Every field an audit row may name, as a dotted path.
 *
 * Written as a literal tuple because it is a vocabulary a reviewer should be
 * able to read in one sitting, and held to the schema by the two assertions
 * below rather than by anybody remembering. Derive-don't-restate is satisfied by
 * the *check*, not by the list: `z.enum` needs literals, and a runtime-derived
 * `z.string().refine(...)` would have cost every consumer the union type.
 */
export const TENANT_EDIT_FIELD_PATHS = [
  'name',
  'email',
  'phone',
  'organisation.legalName',
  'organisation.website',
  'organisation.addressLine1',
  'organisation.addressLine2',
  'organisation.city',
  'organisation.state',
  'organisation.postalCode',
  'organisation.country',
  'organisation.taxId',
  'validTill',
  'isActive',
  'features',
] as const;
export const tenantEditFieldPathSchema = z.enum(TENANT_EDIT_FIELD_PATHS);
export type TenantEditFieldPath = z.infer<typeof tenantEditFieldPathSchema>;

/** The paths {@link tenantEditableFieldsSchema} actually admits. */
type EditablePathFromSchema =
  | Exclude<keyof TenantEditableFields, 'organisation'>
  | `organisation.${Extract<keyof TenantOrganisationEdit, string>}`;

/** An editable field with no audit path — a change that could not be recorded. */
type _EveryEditableFieldHasAPath = AssertTrue<
  EditablePathFromSchema extends TenantEditFieldPath ? true : false
>;
/** An audit path for a field nothing can change — a vocabulary that lies. */
type _EveryPathIsAnEditableField = AssertTrue<
  TenantEditFieldPath extends EditablePathFromSchema ? true : false
>;

/**
 * The paths a request actually asks to change.
 *
 * `undefined` is skipped at both levels, so `{ organisation: {} }` contributes
 * nothing — it is a body that names an object and asks for no change inside it,
 * and counting it as a change would let an empty PATCH write an audit row that
 * says nothing happened.
 *
 * This reports what was *asked for*, not what differed from the stored row. The
 * API compares against the current values and audits only the fields that
 * genuinely moved; see {@link UpdateTenantResponse.changes}.
 */
export function tenantEditFieldPaths(changes: TenantEditableFields): TenantEditFieldPath[] {
  const paths: TenantEditFieldPath[] = [];
  for (const [key, value] of Object.entries(changes)) {
    if (value === undefined) continue;
    if (key === 'organisation') {
      for (const [nested, nestedValue] of Object.entries(value as TenantOrganisationEdit)) {
        if (nestedValue === undefined) continue;
        paths.push(`organisation.${nested}` as TenantEditFieldPath);
      }
      continue;
    }
    paths.push(key as TenantEditFieldPath);
  }
  return paths;
}

/* -------------------------------------------------------------------------- */
/* PATCH /admin/tenants/:tenantId                                              */
/* -------------------------------------------------------------------------- */

/**
 * The tenant-edit request.
 *
 * `changes` and `reason` are siblings rather than one flat object, and that is
 * deliberate: a reason is not a tenant column, and flattening it would put a key
 * inside the strict allow-list that does not correspond to anything in
 * `tenants`. Keeping them apart means {@link tenantEditableFieldsSchema} is
 * exactly the set of tenant fields staff may write — nothing more — which is the
 * property the whole file rests on and the property a reviewer can check by
 * reading one object.
 *
 * An empty `changes` is refused rather than treated as a no-op. A PATCH that
 * changes nothing still writes an audit row under a naive implementation, and an
 * audit table full of rows recording that nothing happened is an audit table
 * nobody reads.
 */
export const updateTenantRequestSchema = z
  .object({
    changes: tenantEditableFieldsSchema,
    reason: adminReasonSchema,
  })
  .strict()
  .superRefine((body, ctx) => {
    if (tenantEditFieldPaths(body.changes).length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['changes'],
        message: 'Name at least one field to change',
      });
    }
  });
export type UpdateTenantRequest = z.infer<typeof updateTenantRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Audit values                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Long enough for the longest editable field, and no longer.
 *
 * `website` accepts 300 characters, so a 256 cap would refuse a legitimate audit
 * row for a legitimate edit. It is a rendering budget, not a payload budget: 320
 * characters cannot hold a contact list, a provider response or a database row.
 *
 * NOTE FOR THE API: this is **larger** than the 256-character limit
 * `modules/integrations/audit-log.writer.ts` enforces on flat `audit_log`
 * metadata values, and that writer *throws* rather than truncating. Slice 8's
 * before/after values belong in their own columns for exactly this reason;
 * routing a 300-character website through `writeAuditEntry`'s `metadata` would
 * refuse the write at the point of no return, after the tenant row had changed.
 */
export const MAX_ADMIN_AUDIT_VALUE_LENGTH = 320;

/**
 * What a before or after value may be: flat and primitive, nothing nested.
 *
 * The same control `AuditMetadata` applies in the API, for the same reason. A
 * nested object is the shape a request body, a provider response or a database
 * row arrives in, and the moment one of those is assignable here, "never write a
 * secret into an append-only table" depends on nobody ever spreading a row in
 * "for context".
 *
 * `.finite()` because `Infinity` is what a division bug produces and it renders
 * as a word in a column of numbers.
 */
export const adminAuditValueSchema = z.union([
  z.string().max(MAX_ADMIN_AUDIT_VALUE_LENGTH),
  z.number().finite(),
  z.boolean(),
  z.null(),
]);
export type AdminAuditValue = z.infer<typeof adminAuditValueSchema>;

/**
 * Renders an enabled feature set as one stable string.
 *
 * De-duplicated and **sorted**, which is the entire point: `['reach', 'flow']`
 * and `['flow', 'reach']` are the same entitlement, and without a canonical
 * order a form that re-orders the checkboxes writes an audit row claiming a
 * change that did not happen. Once that row exists it is permanent and wrong.
 */
export function renderFeatureSet(features: readonly Feature[]): string {
  return [...new Set(features)].sort().join(', ');
}

/**
 * Normalises one scalar tenant field into an audit value.
 *
 * `undefined` collapses to `null` because the database has no `undefined` — a
 * column that is absent from a driver row is a column holding `NULL`, and
 * carrying the distinction any further would give an audit row two ways to say
 * "empty" that a reader could not tell apart.
 *
 * A `Date` becomes its ISO string rather than a number, so `validTill` reads as
 * a date in the same format {@link isoDateTimeSchema} puts on the wire. Two
 * renderings of the same instant in one audit table is how a comparison stops
 * working.
 *
 * It does **not** truncate. A value too long for {@link adminAuditValueSchema}
 * fails parsing, which is loud; a truncated audit value is a quiet lie in a
 * table nobody deletes from.
 */
export function auditValueOf(value: string | number | boolean | Date | null | undefined): AdminAuditValue {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return value.toISOString();
  return value;
}

/**
 * One field that moved, with what it was and what it became.
 *
 * `before` and `after` must differ. A change record where they are equal is
 * either a bug in the diff or a form submitting its own initial values, and
 * either way it fills the audit trail with rows that make the real change harder
 * to find. `Object.is` rather than `===` so the comparison is total over the
 * union — there is no `NaN` here (`.finite()` sees to that) but a comparison
 * that is correct by construction does not need the caveat.
 */
export const adminFieldChangeSchema = z
  .object({
    field: tenantEditFieldPathSchema,
    before: adminAuditValueSchema,
    after: adminAuditValueSchema,
  })
  .strict()
  .superRefine((change, ctx) => {
    if (Object.is(change.before, change.after)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['after'],
        message: `${change.field} did not change; a change record must record a change`,
      });
    }
  });
export type AdminFieldChange = z.infer<typeof adminFieldChangeSchema>;

/* -------------------------------------------------------------------------- */
/* The update response                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The corrected tenant, plus exactly what moved.
 *
 * `changes` is what *differed from the stored row*, not what the request named.
 * Staff submitting a form that re-posts every field, three of which they edited,
 * gets three change records — and gets an empty array when they submitted the
 * form without editing anything.
 *
 * **An empty array is a real answer and the screen must say so in words.** "No
 * field differed from its current value" and "3 fields updated" are different
 * statements; rendering the empty case as a success toast that says "Saved"
 * teaches staff that the button works when it has done nothing, which is how the
 * next genuine failure goes unnoticed.
 */
export const updateTenantResponseSchema = z
  .object({
    tenant: tenantSchema,
    /** Empty when nothing differed. See above — that is not the same as failure. */
    changes: z.array(adminFieldChangeSchema),
  })
  .strict();
export type UpdateTenantResponse = z.infer<typeof updateTenantResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Top-up — a grant on the ledger                                              */
/* -------------------------------------------------------------------------- */

/**
 * The prefix that keeps admin keys out of the message-scoped namespace.
 *
 * `./credit.js` fixes `reserve:` / `consume:` / `release:` as
 * `<type>:<message_id>` and says in as many words that slice 8 must define its
 * own shape and that it **must not** be `<type>:<uuid>` — otherwise
 * `parseCreditIdempotencyKey` would read a top-up back as belonging to a message
 * that does not exist. {@link topupIdempotencyKey} is three segments, so the
 * message parser sees a prefix it does not own *and* an id half that is not a
 * uuid, and returns `null` twice over.
 */
export const TOPUP_KEY_PREFIX = 'topup';

/** A top-up moves at least one credit. Zero and negative are unrepresentable. */
export const MIN_TOPUP_CREDITS = 1;

/**
 * The largest single top-up.
 *
 * A cap exists because `credit_ledger` is append-only: a mistyped extra zero
 * cannot be deleted, only offset by an `adjustment` that is itself permanent, so
 * one slip leaves two rows and a balance history that has to be explained. The
 * cap turns the slip into a refusal. A million credits is a million messages —
 * far beyond any plausible single top-up, so it never obstructs a real one.
 */
export const MAX_TOPUP_CREDITS = 1_000_000;

/**
 * Credits to add. Positive, integral, capped.
 *
 * `.int().min(1)` is what makes SLICES.md criterion 4 — *a top-up of a negative
 * amount is refused* — a property of the type rather than a check somebody has
 * to remember to write. `.max()` is the fat-finger guard above.
 */
export const topupAmountSchema = z
  .number()
  .int('Credits are whole units')
  .min(MIN_TOPUP_CREDITS, 'Top up at least one credit')
  .max(MAX_TOPUP_CREDITS, `A single top-up is capped at ${String(MAX_TOPUP_CREDITS)} credits`);

/**
 * Builds `topup:<tenant_id>:<request_token>`, or throws.
 *
 * **The tenant id is in the key on purpose.** `credit_ledger.idempotency_key` is
 * globally unique, not unique per tenant, so a client token reused across two
 * tenants — a form component that mints one token per mount, a copied cURL, a
 * replayed test fixture — would make the second tenant's top-up hit the unique
 * constraint and no-op. That is the worst possible failure here: a `200`, no
 * ledger row, no audit row, and a customer who paid and did not receive.
 *
 * Throws rather than returning `null`, for the reason `messageScopedKey` throws:
 * both ways of handling a `null` are catastrophic. Skipping the entry loses the
 * top-up silently; inventing a key gives every affected top-up the same one, so
 * the constraint swallows all but the first.
 */
export function topupIdempotencyKey(tenantId: string, requestToken: string): string {
  const tenant = uuidSchema.safeParse(tenantId);
  const token = uuidSchema.safeParse(requestToken);
  if (!tenant.success || !token.success) {
    throw new TypeError(
      `A top-up credit key needs a tenant id and a request token, both uuids, and was given ${
        tenant.success ? 'a request token' : 'a tenant id'
      } that is not one. This is a bug in the caller.`,
    );
  }
  return `${TOPUP_KEY_PREFIX}:${tenant.data}:${token.data}`;
}

/**
 * Reads a top-up key back apart, or `null` for anything else.
 *
 * Refuses a key whose halves are not both uuids, for the same reason
 * `parseCreditIdempotencyKey` does: a lenient parser makes a malformed key look
 * attributable, which is the state {@link topupIdempotencyKey} throws to prevent.
 */
export function parseTopupIdempotencyKey(
  key: string,
): { tenantId: string; requestToken: string } | null {
  const parts = key.split(':');
  if (parts.length !== 3) return null;
  const [prefix, tenantId, requestToken] = parts;
  if (prefix !== TOPUP_KEY_PREFIX) return null;
  const tenant = uuidSchema.safeParse(tenantId);
  const token = uuidSchema.safeParse(requestToken);
  if (!tenant.success || !token.success) return null;
  return { tenantId: tenant.data, requestToken: token.data };
}

/**
 * The top-up request.
 *
 * **The caller supplies the idempotency token, and that is a deliberate choice
 * over deriving one server-side.** The guarantee a key buys is protection
 * against a retry the server cannot tell apart from a new request — a
 * double-clicked button, a proxy retry, a refresh after a gateway timeout. Only
 * the caller knows those two HTTP requests were one intent.
 *
 * A server-derived key has to be a hash of the request content, and both
 * available variants fail in the direction that costs money:
 *
 *   - hash of `(tenant, channel, amount, reason)` — two genuinely separate
 *     top-ups of the same amount, for the same stated reason, collapse into one.
 *     The second returns `200`, moves nothing, and the tenant is short.
 *   - the same hash plus a time bucket — the deduplication stops working the
 *     moment a retry crosses the bucket boundary, which is precisely when a
 *     retry happens, because the thing that caused it was slow.
 *
 * So the admin app mints a uuid when it opens the top-up form and sends the same
 * one on every submit of that form. A fresh form is a fresh top-up; a retry of
 * the same form is not.
 *
 * There is no `balance` field and no way to express one. A top-up says how much
 * to move, never what the result should be.
 */
export const tenantTopupRequestSchema = z
  .object({
    channel: channelSchema,
    amount: topupAmountSchema,
    /**
     * {@link topupReasonSchema}, not {@link adminReasonSchema}: this one is
     * capped at 256 because it is recorded in `audit_log.metadata` rather than
     * in `admin_audit.reason`. Both apps parse against this schema, so the
     * narrower cap is knowable before the request is sent instead of arriving as
     * a 400 after a two-step confirmation.
     */
    reason: topupReasonSchema,
    /**
     * Minted by the caller, once per top-up intent. Reused verbatim on retry.
     * Becomes the id half of {@link topupIdempotencyKey}.
     */
    requestToken: uuidSchema,
  })
  .strict();
export type TenantTopupRequest = z.infer<typeof tenantTopupRequestSchema>;

/**
 * What the top-up did — including "nothing, it was already there".
 *
 * `applied` is the field SLICES.md criterion 3 is about. `true` means *this*
 * call inserted the ledger row (`201`); `false` means the key already existed
 * and this call moved nothing (`200`), and the balance and `ledgerEntryId`
 * returned are the ones the original top-up produced. A response that could not
 * tell those apart would make "replaying it moves nothing" unobservable from
 * outside, which is the same as unverifiable.
 *
 * `channel` is not repeated at the top level: it is on `balance`, and a payload
 * carrying the same value twice is a payload that can contradict itself. Same
 * argument as `available` never being a field.
 */
export const tenantTopupResponseSchema = z
  .object({
    /** Credits the request asked to move. */
    amount: topupAmountSchema,
    /** True only when this call inserted the ledger row. See above. */
    applied: z.boolean(),
    /** `topup:<tenant_id>:<request_token>`. Unique across `credit_ledger`. */
    idempotencyKey: creditIdempotencyKeySchema,
    /** The `grant` entry — the one written now, or the one already there. */
    ledgerEntryId: uuidSchema,
    /** The channel's balance as it stands after the movement. Carries `channel`. */
    balance: creditBalanceSchema,
  })
  .strict();
export type TenantTopupResponse = z.infer<typeof tenantTopupResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The audit record                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The two things a staff mutation can be, as a closed vocabulary.
 *
 * Dotted verbs, matching the slice-0 and slice-3 audit vocabulary
 * (`tenant.created`, `integration.connected`).
 *
 * `tenant.credits_granted` names the **ledger entry type it wrote** rather than
 * the operator's word for it. The request and response above say "top-up",
 * because that is what the person is doing; the audit row says "granted",
 * because that is the row it produced and the word somebody re-summing the
 * ledger will be searching for. The mismatch is deliberate and should not be
 * "fixed" in either direction: `./credit.js` already uses both words for this
 * one movement.
 */
export const ADMIN_AUDIT_ACTION_VALUES = ['tenant.updated', 'tenant.credits_granted'] as const;
export const adminAuditActionSchema = z.enum(ADMIN_AUDIT_ACTION_VALUES);
export type AdminAuditAction = z.infer<typeof adminAuditActionSchema>;

/**
 * Who did it.
 *
 * **Two fields, on purpose.** `adminUserId` is a live pointer and every audit
 * foreign key in this schema is `ON DELETE SET NULL`, so deactivating or
 * removing a staff account would erase the attribution on every row they ever
 * wrote — which is the one thing an audit trail must survive. `adminEmail` is a
 * literal copied at write time and is never nulled, so the row still answers
 * "who" after the account is gone.
 *
 * The rejected alternative was `ON DELETE RESTRICT` on the pointer, keeping one
 * field. It makes the audit table a reason staff accounts can never be removed,
 * which turns a compliance feature into an offboarding blocker, and the pressure
 * then goes on deleting audit rows.
 */
export const adminAuditActorSchema = z
  .object({
    /** Null once the staff account is gone. Never null at write time. */
    adminUserId: uuidSchema.nullable(),
    /** Copied at write time. Survives the pointer being nulled. */
    adminEmail: z.string().email(),
  })
  .strict();
export type AdminAuditActor = z.infer<typeof adminAuditActorSchema>;

/**
 * What every audit record carries regardless of what happened.
 *
 * `id` is an opaque string rather than a number: the existing `audit_log` uses a
 * `bigint` identity, and a bigint that crosses JSON as a number is a bigint that
 * silently loses precision past 2^53. Whatever key the slice-8 table uses, it
 * reaches the wire as text.
 */
const adminAuditBase = {
  id: z.string().min(1),
  tenantId: uuidSchema,
  actor: adminAuditActorSchema,
  /** Required. There is no audit record without one — see {@link adminReasonSchema}. */
  reason: adminReasonSchema,
  createdAt: isoDateTimeSchema,
};

/**
 * A tenant edit, as one row per audited mutation carrying every field that
 * moved.
 *
 * `changes` is `min(1)`: a record of an edit that changed nothing is not an
 * audit record, and writing one is how an audit table becomes noise. The API
 * therefore writes **no** row when the diff is empty, and the response says so
 * with an empty `changes` array rather than a fabricated record.
 */
const tenantUpdatedAuditObject = z
  .object({
    ...adminAuditBase,
    action: z.literal('tenant.updated'),
    changes: z.array(adminFieldChangeSchema).min(1, 'An audit record must record a change'),
  })
  .strict();
export const tenantUpdatedAuditRecordSchema = tenantUpdatedAuditObject;
export type TenantUpdatedAuditRecord = z.infer<typeof tenantUpdatedAuditObject>;

const tenantCreditsGrantedAuditObject = z
  .object({
    ...adminAuditBase,
    action: z.literal('tenant.credits_granted'),
    channel: channelSchema,
    amount: topupAmountSchema,
    /** Ties the row to the append-only ledger entry it caused. */
    idempotencyKey: creditIdempotencyKeySchema,
    ledgerEntryId: uuidSchema,
    /**
     * The cached balance either side of the movement.
     *
     * Recorded because it is **what the operator saw**, which is the thing a
     * "where did this number come from" conversation actually needs, and not
     * because it is a source of truth — the ledger is, and re-summing it is
     * {@link creditReconciliationSchema}'s job.
     */
    balanceBefore: z.number().int().min(0),
    balanceAfter: z.number().int().min(0),
  })
  .strict();

/**
 * The one arithmetic invariant an audit row can be checked against.
 *
 * A `grant` is applied inside the same transaction that writes this row, so
 * `after` is `before + amount` by construction. If it is not, the row is
 * describing a movement that did not happen, and it should fail parsing here
 * rather than be filed away as evidence.
 *
 * Written once and used from both the standalone schema and the union below,
 * because a check that exists in only one of two parse paths is a check that is
 * absent exactly when somebody parses the other way.
 */
function refineCreditsGrantedAudit(
  record: z.infer<typeof tenantCreditsGrantedAuditObject>,
  ctx: z.RefinementCtx,
): void {
  if (record.balanceAfter !== record.balanceBefore + record.amount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['balanceAfter'],
      message:
        'The balance after a grant must be the balance before it plus the amount granted',
    });
  }
}

export const tenantCreditsGrantedAuditRecordSchema =
  tenantCreditsGrantedAuditObject.superRefine(refineCreditsGrantedAudit);
export type TenantCreditsGrantedAuditRecord = z.infer<typeof tenantCreditsGrantedAuditObject>;

/**
 * Every staff mutation, as a discriminated union on `action`.
 *
 * A union rather than one wide object with nullable columns, because the wide
 * object can express a tenant edit that also carries an `amount` and a
 * `ledgerEntryId` — a row nothing produces and nobody would keep accurate. The
 * union members are plain objects (zod's `discriminatedUnion` will not accept a
 * refined one), so the grant's arithmetic check is re-applied here through the
 * same function the standalone schema uses.
 */
export const adminAuditRecordSchema = z
  .discriminatedUnion('action', [tenantUpdatedAuditObject, tenantCreditsGrantedAuditObject])
  .superRefine((record, ctx) => {
    if (record.action === 'tenant.credits_granted') refineCreditsGrantedAudit(record, ctx);
  });
export type AdminAuditRecord = z.infer<typeof adminAuditRecordSchema>;

export const adminAuditListQuerySchema = paginationQuerySchema.extend({
  tenantId: uuidSchema.optional(),
  action: adminAuditActionSchema.optional(),
  adminUserId: uuidSchema.optional(),
});
export type AdminAuditListQuery = z.infer<typeof adminAuditListQuerySchema>;

export const adminAuditListResponseSchema = paginated(adminAuditRecordSchema);
export type AdminAuditListResponse = z.infer<typeof adminAuditListResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Reconciliation — the report, per tenant and channel                         */
/* -------------------------------------------------------------------------- */

/** Room for a driver message, nowhere near room for a payload. */
export const MAX_RECONCILIATION_REASON_LENGTH = 500;

const reconciliationReasonSchema = z
  .string()
  .trim()
  .min(1, 'Say why this tenant was not checked')
  .max(MAX_RECONCILIATION_REASON_LENGTH);

/**
 * One tenant that **was** checked.
 *
 * `channels` carries one {@link creditReconciliationSchema} per channel in
 * `CHANNEL_VALUES`, always, zeros included — a tenant that has never had a
 * WhatsApp credit reconciles to `0 = 0`, and saying so is different from leaving
 * the channel out. `min(1)` because a "checked" tenant with no channels checked
 * is the `not_checked` case wearing the wrong label.
 *
 * There is no `inSync` field here. It is `channels.every(c => c.inSync)` and it
 * is {@link tenantReconciliationInSync} — the same argument as `available` never
 * being a stored field, with the extra edge that a stored `inSync` beside a
 * drifting channel is a green tick over a red number.
 */
const tenantReconciliationCheckedObject = z
  .object({
    outcome: z.literal('checked'),
    tenantId: uuidSchema,
    /** So the alarm can name the tenant without a second query. */
    tenantName: z.string(),
    channels: z.array(creditReconciliationSchema).min(1),
  })
  .strict();
export type TenantReconciliationChecked = z.infer<typeof tenantReconciliationCheckedObject>;

/**
 * One tenant that was **not** checked, and why.
 *
 * This member is the reason the whole thing is a union. Without it a tenant
 * whose re-sum timed out simply does not appear in the results, and a run that
 * silently skipped four tenants reports the same "zero drift" as a run that
 * checked every one. `reason` is required and non-empty for the same reason the
 * mutation reason is: an unexplained skip is the one that gets ignored.
 */
const tenantReconciliationNotCheckedObject = z
  .object({
    outcome: z.literal('not_checked'),
    tenantId: uuidSchema,
    tenantName: z.string(),
    reason: reconciliationReasonSchema,
  })
  .strict();
export type TenantReconciliationNotChecked = z.infer<typeof tenantReconciliationNotCheckedObject>;

/**
 * **Checked-and-agreed, checked-and-drifted, and not-checked are three states.**
 *
 * The union gives the third one a place to live; `inSync` on the checked member
 * separates the first two. A boolean `ok` per tenant would have collapsed
 * "agreed" and "never looked" into the same `false`-is-bad reading, and the way
 * that fails is that the run looks healthier than it is.
 */
export const tenantReconciliationSchema = z.discriminatedUnion('outcome', [
  tenantReconciliationCheckedObject,
  tenantReconciliationNotCheckedObject,
]);
export type TenantReconciliation = z.infer<typeof tenantReconciliationSchema>;

/** Every channel agreed. Derived, never stored — see the checked member. */
export function tenantReconciliationInSync(result: TenantReconciliationChecked): boolean {
  return result.channels.every((channel) => channel.inSync);
}

/**
 * One run of the reconciliation job.
 *
 * A run is a **report** in the sense the architecture rules use the word: a
 * deliberate snapshot, so it records when it started and when it finished rather
 * than being recomputed on read.
 *
 * `aborted` exists because the tenant *enumeration* can fail too, and an aborted
 * run holding an empty `tenants` array is indistinguishable from a completed run
 * over a platform with no tenants. Both would report zero drift; only one of
 * them checked anything. An aborted run still carries whatever it managed before
 * it stopped — partial results are evidence, and discarding them to keep the
 * shape simple would throw away the half of the run that worked.
 *
 * There is deliberately **no summary object on the wire and no `repaired`
 * field.** The summary is {@link reconciliationRunSummary}, one derivation for
 * the job's alarm and the admin screen both — a reconciliation whose two readers
 * disagree is a reconciliation that gets explained away rather than fixed. And
 * nothing in this shape can say a drift was corrected, because the job must not
 * correct one: a ledger and a balance disagreeing is either a bug we need to see
 * or a manual correction somebody made, and quietly repairing both makes the
 * first invisible and the second unattributable.
 */
const reconciliationRunBase = {
  runId: uuidSchema,
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema,
  tenants: z.array(tenantReconciliationSchema),
};

const reconciliationRunCompletedObject = z
  .object({ ...reconciliationRunBase, outcome: z.literal('completed') })
  .strict();

const reconciliationRunAbortedObject = z
  .object({
    ...reconciliationRunBase,
    outcome: z.literal('aborted'),
    /** Why the run stopped. Required — see the `not_checked` member above. */
    reason: reconciliationReasonSchema,
  })
  .strict();

export const creditReconciliationRunSchema = z.discriminatedUnion('outcome', [
  reconciliationRunCompletedObject,
  reconciliationRunAbortedObject,
]);
export type CreditReconciliationRun = z.infer<typeof creditReconciliationRunSchema>;

export const creditReconciliationRunResponseSchema = z
  .object({ run: creditReconciliationRunSchema })
  .strict();
export type CreditReconciliationRunResponse = z.infer<typeof creditReconciliationRunResponseSchema>;

/** A drifting channel, with the tenant it belongs to. What the alarm lists. */
export interface ReconciliationDrift {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly reconciliation: CreditReconciliation;
}

/** Every channel that did not agree, across every tenant the run checked. */
export function reconciliationDrifts(run: CreditReconciliationRun): ReconciliationDrift[] {
  const drifts: ReconciliationDrift[] = [];
  for (const tenant of run.tenants) {
    if (tenant.outcome !== 'checked') continue;
    for (const reconciliation of tenant.channels) {
      if (reconciliation.inSync) continue;
      drifts.push({
        tenantId: tenant.tenantId,
        tenantName: tenant.tenantName,
        reconciliation,
      });
    }
  }
  return drifts;
}

export interface ReconciliationRunSummary {
  readonly tenantsChecked: number;
  /** Tenants the run knew about and did not check. Never silently zero. */
  readonly tenantsNotChecked: number;
  readonly tenantsInSync: number;
  readonly tenantsWithDrift: number;
  readonly channelsWithDrift: number;
  /**
   * Whether a person needs to look.
   *
   * True when the run aborted, when **anything was not checked**, or when any
   * channel drifted. A skip counts: a reconciliation that could not read four
   * tenants has not established that those four are fine, and a summary that
   * treated silence as agreement would be the exact failure this report is
   * shaped to prevent.
   */
  readonly needsAttention: boolean;
}

/**
 * The only supported way to summarise a run.
 *
 * Shared rather than duplicated in the job and the admin screen, for the reason
 * `rateReading` is shared: two implementations of "did this run find anything"
 * drift, and the way they drift is that one of them stops counting the skips.
 */
export function reconciliationRunSummary(run: CreditReconciliationRun): ReconciliationRunSummary {
  let tenantsChecked = 0;
  let tenantsNotChecked = 0;
  let tenantsInSync = 0;
  let tenantsWithDrift = 0;

  for (const tenant of run.tenants) {
    if (tenant.outcome === 'not_checked') {
      tenantsNotChecked += 1;
      continue;
    }
    tenantsChecked += 1;
    if (tenantReconciliationInSync(tenant)) tenantsInSync += 1;
    else tenantsWithDrift += 1;
  }

  const channelsWithDrift = reconciliationDrifts(run).length;

  return {
    tenantsChecked,
    tenantsNotChecked,
    tenantsInSync,
    tenantsWithDrift,
    channelsWithDrift,
    needsAttention:
      run.outcome === 'aborted' || tenantsNotChecked > 0 || tenantsWithDrift > 0,
  };
}
